import React, { useEffect } from 'react'
import {
  Avatar,
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Download, Search } from '@mui/icons-material'
import dayjs from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useSubscriptionsManagement } from '../../../hooks/useSubscriptionsManagement'
import { formatTimestamp } from '../../../utils/dateUtils'
import { getProxiedUrl } from '../../../utils/userUtils'

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
  success: '#2e7d32',
  danger: '#d32f2f',
}

const statusOptions = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
]

const getStatusChipStyles = (status) => {
  const normalized = (status || '').toUpperCase()
  if (normalized === 'ACTIVE') {
    return {
      bgcolor: '#e8f5e9',
      color: themeColors.success,
      borderColor: '#c8e6c9',
    }
  }
  if (normalized === 'SCHEDULED') {
    return {
      bgcolor: '#fff8e1',
      color: '#ef6c00',
      borderColor: '#ffe0b2',
    }
  }
  if (normalized === 'CANCELLED') {
    return {
      bgcolor: '#f3e5f5',
      color: '#7b1fa2',
      borderColor: '#e1bee7',
    }
  }
  return {
    bgcolor: '#ffebee',
    color: themeColors.danger,
    borderColor: '#ffcdd2',
  }
}

const formatAmount = (amount, currency = 'AED') => {
  const value = Number(amount)
  if (!Number.isFinite(value)) return '-'
  return `${currency} ${value.toFixed(2)}`
}

const formatCountries = (countries) => {
  if (!Array.isArray(countries) || countries.length === 0) {
    return '-'
  }
  return countries.includes('ALL') ? 'All GCC Countries' : countries.join(', ')
}

const getUserInitial = (name, email) => {
  if (name && name.trim()) {
    return name.trim().charAt(0).toUpperCase()
  }
  if (email && email.trim()) {
    return email.trim().charAt(0).toUpperCase()
  }
  return 'U'
}

const SubscriptionsTab = ({ showNotification, onCountChange }) => {
  const {
    subscriptions,
    loading,
    exporting,
    currentPage,
    totalPages,
    totalItems,
    statusCounts,
    searchTerm,
    statusFilter,
    fromDate,
    toDate,
    setSearchTerm,
    setStatusFilter,
    setFromDate,
    setToDate,
    loadSubscriptions,
    applyFilters,
    resetFilters,
    handlePageChange,
    exportSubscriptions,
  } = useSubscriptionsManagement()

  useEffect(() => {
    loadSubscriptions().catch((error) => {
      const message = error?.response?.data?.message || 'Failed to load subscriptions'
      showNotification(message, 'error')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (onCountChange) {
      onCountChange(totalItems)
    }
  }, [onCountChange, totalItems])

  const handleApply = async () => {
    try {
      await applyFilters()
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to apply filters'
      showNotification(message, 'error')
    }
  }

  const handleReset = async () => {
    try {
      await resetFilters()
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to reset filters'
      showNotification(message, 'error')
    }
  }

  const handleExport = async () => {
    try {
      const count = await exportSubscriptions()
      showNotification(`Exported ${count} subscription records`, 'success')
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'CSV export failed'
      showNotification(message, 'error')
    }
  }

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleApply()
    }
  }

  const dateFieldSx = {
    minWidth: 170,
    '& .MuiInputLabel-root': {
      color: '#64748b',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: themeColors.primary,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#cbd5e1',
      },
      '&:hover fieldset': {
        borderColor: themeColors.primary,
      },
      '&.Mui-focused fieldset': {
        borderColor: themeColors.primary,
      },
    },
    '& .MuiSvgIcon-root': {
      color: themeColors.primary,
    },
  }

  const datePickerPopperSx = {
    '& .MuiPaper-root': {
      borderRadius: 2,
      border: `1px solid ${themeColors.primary}`,
      boxShadow: '0 10px 26px rgba(35, 37, 54, 0.16)',
    },
    '& .MuiPickersCalendarHeader-label': {
      color: themeColors.primary,
      fontWeight: 700,
    },
    '& .MuiDayCalendar-weekDayLabel': {
      color: themeColors.primary,
      fontWeight: 600,
    },
    '& .MuiPickersArrowSwitcher-button, & .MuiPickersCalendarHeader-switchViewButton': {
      color: themeColors.primary,
    },
    '& .MuiPickersDay-root': {
      color: themeColors.primary,
      fontWeight: 500,
    },
    '& .MuiPickersDay-root:hover': {
      backgroundColor: 'rgba(35, 37, 54, 0.12)',
    },
    '& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: themeColors.primary,
      color: '#ffffff',
    },
    '& .MuiPickersDay-root.Mui-selected:hover': {
      backgroundColor: themeColors.hover,
    },
    '& .MuiPickersDay-root.MuiPickersDay-today': {
      borderColor: themeColors.primary,
      borderWidth: '1px',
      borderStyle: 'solid',
    },
    '& .MuiPickersDay-root.Mui-disabled': {
      color: '#c0c8d2',
    },
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant='h5' sx={{ fontWeight: 600 }}>
          Subscription Management
        </Typography>
        <Button
          variant='contained'
          startIcon={<Download />}
          onClick={handleExport}
          disabled={exporting}
          sx={{
            bgcolor: themeColors.primary,
            color: 'white',
            '&:hover': { bgcolor: themeColors.hover },
            px: 3,
            py: 1,
          }}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {statusOptions
          .filter((item) => item.value !== 'ALL')
          .map((item) => (
            <Chip
              key={item.value}
              label={`${item.label}: ${statusCounts?.[item.value] ?? 0}`}
              size='small'
              variant='outlined'
            />
          ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <TextField
          size='small'
          label='Search'
          placeholder='User name/email or plan'
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          sx={{ minWidth: 280, flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search sx={{ fontSize: 18, color: '#6b7280' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size='small'
          label='Status'
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          {statusOptions.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>

        <DatePicker
          label='From'
          format='DD-MM-YYYY'
          value={fromDate ? dayjs(fromDate) : null}
          onChange={(value) =>
            setFromDate(value && value.isValid() ? value.format('YYYY-MM-DD') : '')
          }
          slotProps={{
            textField: {
              size: 'small',
              sx: dateFieldSx,
            },
            popper: {
              sx: datePickerPopperSx,
            },
          }}
        />

        <DatePicker
          label='To'
          format='DD-MM-YYYY'
          value={toDate ? dayjs(toDate) : null}
          onChange={(value) =>
            setToDate(value && value.isValid() ? value.format('YYYY-MM-DD') : '')
          }
          slotProps={{
            textField: {
              size: 'small',
              sx: dateFieldSx,
            },
            popper: {
              sx: datePickerPopperSx,
            },
          }}
        />

        <Button
          variant='contained'
          onClick={handleApply}
          sx={{
            bgcolor: themeColors.primary,
            color: 'white',
            '&:hover': { bgcolor: themeColors.hover },
            px: 3,
            py: 1,
          }}
        >
          Apply
        </Button>
        <Button
          variant='outlined'
          onClick={handleReset}
          sx={{
            color: themeColors.primary,
            borderColor: themeColors.primary,
            '&:hover': {
              borderColor: themeColors.primary,
              bgcolor: '#f8fafc',
            },
          }}
        >
          Reset
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 1, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Purchased</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Start</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>End</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Auto-Renew</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align='center'>
                  <Typography variant='body2' color='text.secondary' sx={{ py: 3 }}>
                    Loading subscriptions...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : subscriptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align='center'>
                  <Typography variant='body2' color='text.secondary' sx={{ py: 3 }}>
                    No subscriptions found for the selected filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              subscriptions.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar
                        src={getProxiedUrl(item.profilePictureUrl)}
                        alt={item.userName || item.userEmail || 'User'}
                        imgProps={{
                          onError: (event) => {
                            event.target.src = ''
                          },
                          referrerPolicy: 'no-referrer',
                        }}
                        sx={{
                          width: 34,
                          height: 34,
                          bgcolor: themeColors.primary,
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: '0.88rem',
                        }}
                      >
                        {getUserInitial(item.userName, item.userEmail)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant='body2' sx={{ fontWeight: 600 }} noWrap>
                          {item.userName || 'Unknown User'}
                        </Typography>
                        <Typography variant='caption' color='text.secondary' noWrap>
                          {item.userEmail || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      {item.planName || '-'}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {item.planCode || '-'}
                    </Typography>
                    <Tooltip title={formatCountries(item.countries)} arrow>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{
                          display: 'block',
                          maxWidth: 220,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatCountries(item.countries)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size='small'
                      variant='outlined'
                      label={item.status || 'UNKNOWN'}
                      sx={getStatusChipStyles(item.status)}
                    />
                  </TableCell>
                  <TableCell>{formatTimestamp(item.purchasedAt)}</TableCell>
                  <TableCell>{formatTimestamp(item.startDate)}</TableCell>
                  <TableCell>{formatTimestamp(item.endDate)}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {formatAmount(item.amountPaid, item.currency || 'AED')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size='small'
                      label={item.autoRenew ? 'ON' : 'OFF'}
                      color={item.autoRenew ? 'success' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mt: 3,
            gap: 2,
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, page) => {
              handlePageChange(page).catch((error) => {
                const message =
                  error?.response?.data?.message || 'Failed to change page'
                showNotification(message, 'error')
              })
            }}
            color='primary'
            showFirstButton
            showLastButton
          />
          <Typography variant='body2' color='text.secondary'>
            Total: {totalItems}
          </Typography>
        </Box>
      )}
      </Box>
    </LocalizationProvider>
  )
}

export default SubscriptionsTab
